import { Token } from "@app/di/token";
import { BundleLoaderService } from "@app/services/bundle-loader.service";
import { Constructor, IMap } from "@tools";

export interface INgInjectResolve {
    name: string;
    dependencies?: Token[];
    fn?: Function;
}

export interface INgInjectArgs {
    token: Token;
    /**
     * inherit the dependencies and resolves of another object.
     * Useful for derived controllers.
     */
    inherit?: Token[];
    dependencies?: Token[];
    resolves?: INgInjectResolve[];
    libraries?: Token[];
}

export interface INgInject {
    /**
     * The angular type... like service, controller, directive, etc.
     */
    ngType: string;
    token: Token;
    dependencies: Token[];
    resolves?: INgInjectResolve[];
    construct: any[];
    resolveObject: { [name: string]: Function };
    libraries?: Token[];
    addedToApp: boolean;
}

export function NgService(args: INgInjectArgs) {
    return (target: Constructor<any>) => {
        DI.register("service", args, target);
    };
}
export function NgAnimation(args: INgInjectArgs) {
    return (target: Constructor<any>) => {
        DI.register("animation", args, target);
    };
}
export function NgComponent(args: INgInjectArgs) {
    return (target: Constructor<any>) => {
        DI.register("directive", args, target);
    };
}
export function NgInterceptor(args: INgInjectArgs) {
    return (target: Constructor<any>) => {
        DI.register("factory", args, target);
    };
}
export function NgFilter(args: INgInjectArgs) {
    return (target: Constructor<any>) => {
        DI.register("filter", args, target);
    };
}
export function NgView(args: INgInjectArgs) {
    return (target: Constructor<any>) => {
        DI.register("controller", args, target);
    };
}

export class DI {
    private static db: IMap<INgInject> = {};

    public static register(ngType: string, args: INgInjectArgs, constructor: Constructor<any>) {
        if (DI.db.hasOwnProperty(args.token.key)) {
            // Phase 2 (Vue migration): when AngularHost mounts/unmounts, angular
            // re-bootstraps and oc.lazyLoad's per-injector cache is fresh, so it
            // re-fetches + re-evaluates admin.js. The IIFE re-runs and decorator
            // calls land here with tokens that already exist in this static
            // `DI.db`. Throwing would kill the second mount. Instead we detect
            // the re-evaluation and flip `addedToApp` back to false so the
            // subsequent `DI.fillAngularApp(kbadmin)` call (at the top of
            // admin.ts, which also redefines the kbadmin module via
            // `angular.module('kbadmin', [...])` — the deps-array form wipes
            // existing registrations) re-wires all services onto the fresh
            // module. The existing `INgInject` entry is kept with its original
            // constructor reference: the new class object produced by the
            // re-evaluation is functionally equivalent (same source), and keeps
            // `construct[i.ngType](i.token.key, i.construct)` stable across
            // mounts. If a different decorator genuinely tried to claim the
            // same token mid-session, flagging that is now the responsibility
            // of build-time / code-review rather than runtime.
            //
            // Legacy single-bootstrap mode never hits this path; the first
            // registration is still the only registration.
            const existing = DI.db[args.token.key];
            if (existing.ngType !== ngType) {
                // Really-a-mistake case: same token claimed as a service AND a
                // filter, for example. Keep the hard error for this.
                throw new Error(
                    `A type with key ${args.token.key} has already been registered as ${existing.ngType}, cannot re-register as ${ngType}`,
                );
            }
            existing.addedToApp = false;
            return;
        }
        args.dependencies = args.dependencies || [];
        args.resolves = args.resolves || [];
        args.libraries = args.libraries || [];

        if (args.inherit) {
            for (let baseToken of args.inherit) {
                let baseInject = DI.get(baseToken);
                if (baseInject.dependencies) args.dependencies.unshift(...baseInject.dependencies);
                if (baseInject.resolves) args.resolves.unshift(...baseInject.resolves);
                if (baseInject.libraries) args.libraries.unshift(...baseInject.libraries);
            }
        }

        let resolveObject = args.resolves &&
            args.resolves
                .reduce((p, c) => {
                    p[c.name] = [].concat(...c.dependencies.map(d => d.key)).concat(c.fn.bind(constructor));
                    return p;
                }, {});
        if (args.libraries) {
            resolveObject["scripts"] = [Token.BundleLoaderService.key, (bundleLoader: BundleLoaderService) => {
                return bundleLoader.load(args.libraries);
            }];
        }

        let ngInject = {
            token: args.token,
            ngType,
            dependencies: args.dependencies,
            resolves: args.resolves,
            construct: [].concat(args.dependencies.map(d => d.key)).concat((...a) => new constructor(...a)),
            resolveObject: resolveObject,
            libraries: args.libraries
        } as INgInject;
        DI.db[args.token.key] = ngInject;
    }

    public static get(token: Token): INgInject {
        return DI.db[token.key];
    }

    public static fillAngularApp(app: ng.IModule) {
        for (let key in DI.db) {
            let i = DI.db[key];
            if (!i.addedToApp) {
                app[i.ngType](i.token.key, i.construct);
                i.addedToApp = true;
            }    
        }
    }
}
